import {
    type LatestProcessSolveStandardizedOutput,
    type NodeCfg,
    type NodeControlSocketKindMap,
    type NodeDataSocketKindMap,
    type NodeSocketKind,
    type NodeSocketKindMap,
} from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { NodeSpec } from './NodeSpec';
import { gqlClient } from 'src/util/gql-client';
import { GetProcessByIdForProcessCollectNodeDocument } from 'types/db';
import { ProcessDataElementCollectionKindEnum, ProcessDataElementKindEnum } from '@redviking/argonaut-util/types/db-enums/process';
import type { FlowNodeInput } from '../../sockets/FlowNodeInput';
import type { FlowNodeOutput } from '../../sockets/FlowNodeOutput';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import { defaultControlUpdateValueHandler } from '@redviking/argonaut-core-ui/src/applet/design/flows/controls/control-event-handler';

const deletableOutputHelper = <S extends NodeSocketKind>(payload: {
    label: string;
    value: keyof NodeSocketKindMap['processSolve']['outputs'];
    nodeCfg: NodeCfg<'processSolve'>;
    outputType: S;
}): FlowNodeInput.Opts<S, null> => {
    return {
        socket: payload.outputType,
        controlOpts: defineControlOpts({
            cfg: {
                rightAlign: true,
                label: payload.label,
                kind: 'deletableSocket',
            },
            enableWithConnectedSocket: true,
            on: {
                'update:value': () => {}, // no-op, this control does not use the update:value event
                'delete': (_, { flowEventEmitter }) => flowEventEmitter.emit('removeNodeSocket', {
                    direction: 'output',
                    removalMethod: {
                        kind: 'uniqueIdentifier',
                        value: payload.value,
                    },
                    newNodeCfg: {
                        ...payload.nodeCfg,
                        controlValues: {
                            ...payload.nodeCfg.controlValues,
                            useStandardizedOutputs: {
                                ...payload.nodeCfg.controlValues.useStandardizedOutputs,
                                [payload.value]: false,
                            },
                        },
                    },
                }),
            },
            value: null,
        }),
    };
};

const processSolveColumnLabels = {
    solveFailReason: 'Solve Fail Reason',
} satisfies {
    [key in keyof LatestProcessSolveStandardizedOutput]: string;
};

const processSolveColumnSelectionMap = Object.entries(processSolveColumnLabels).map(([ key, value ]) => ({
    label: value,
    value: key,
}));

export const processSolveUiNodeSpec: NodeSpec<NodeCfg<'processSolve'>> = {
    nodeCategory: 'argonautApi',
    requiredSockets: [ 'materialId' ],
    kindLabel: 'Process Solve',
    controls: nodeCfg => {
        const ret: ReturnType<NodeSpec<NodeCfg<'processSolve'>>['controls']> = {
            processId: defineControlOpts({
                value: nodeCfg.controlValues.processId,
                cfg: {
                    kind: 'selectProcess',
                    label: 'Process',
                },
                on: {
                    'update:value': async (processId, { flowEventEmitter }) => {
                        const {
                            process_process_by_pk,
                        } = await gqlClient.request({
                            document: GetProcessByIdForProcessCollectNodeDocument,
                            variables: {
                                processId,
                            },
                        });
                        if (!process_process_by_pk) {
                            throw new Error(`Process with ID ${processId} not found.`);
                        }

                        const processParams: NodeCfg<'processSolve'>['controlValues']['processParameters'] = [];
                        for (const dataElement of process_process_by_pk.data_elements) {
                            if (dataElement.collection_kind === ProcessDataElementCollectionKindEnum.SpecParam) {
                                const dataElementKind = dataElement.kind;
                                if (dataElementKind === ProcessDataElementKindEnum.Boolean || dataElementKind === ProcessDataElementKindEnum.Number || dataElementKind === ProcessDataElementKindEnum.String) {
                                    processParams.push({
                                        id: dataElement.id,
                                        type: dataElementKind,
                                        name: dataElement.name,
                                    });
                                } else if (dataElementKind === ProcessDataElementKindEnum.MaterialClass || dataElementKind === ProcessDataElementKindEnum.MaterialModel) {
                                    processParams.push({
                                        id: dataElement.id,
                                        name: dataElement.name,
                                        type: ProcessDataElementKindEnum.String,
                                    });
                                }
                            }
                        }
                        const newNodeCfg: NodeCfg<'processSolve'> = {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                processId,
                                processParameters: processParams,
                            },
                        };
                        flowEventEmitter.emit('updateNodeCfg', {
                            newNodeCfg,
                        });
                    },
                },
            }),
            location: defineControlOpts({
                value: nodeCfg.controlValues.location,
                cfg: {
                    kind: 'location',
                },
                on: { 'update:value': defaultControlUpdateValueHandler },
            }),
            useStandardizedOutputs: defineControlOpts({
                    value: nodeCfg.controlValues.useStandardizedOutputs,
                    cfg: {
                        selectLabel: 'Add Output',
                        kind: 'selectWithCallback',
                        items: () => {
                            const notUsedOutputs = Object.entries(nodeCfg.controlValues.useStandardizedOutputs).filter(([ , value ]) => !value).map(([ key ]) => key);
                            return processSolveColumnSelectionMap.filter(item => notUsedOutputs.includes(item.value));
                        },
                    },
                    on: {
                        'update:value': () => {}, // no-op, we only care about the select event
                        'select': (value, { flowEventEmitter }) => {
                            const typedValue = value as keyof typeof nodeCfg.controlValues.useStandardizedOutputs;
                            flowEventEmitter.emit('updateNodeCfg', {
                                newNodeCfg: {
                                    ...nodeCfg,
                                    controlValues: {
                                        ...nodeCfg.controlValues,
                                        useStandardizedOutputs: {
                                            ...nodeCfg.controlValues.useStandardizedOutputs,
                                            [typedValue]: true,
                                        },
                                    },
                                },
                            });
                        },
                    },
                }),
        };
        return ret;
    },
    cfg: () => {
        const processSolveNodeCfg: NodeCfg<'processSolve'> = {
            disabled: false,
            id: getUID(),
            kind: 'processSolve',
            width: 210,
            controlValues: {
                processId: '',
                processParameters: [],
                location: {
                    id: '',
                },
                useStandardizedOutputs: {
                    solveFailReason: true,
                },
            },
        };
        return processSolveNodeCfg;
    },
    inputs: _nodeCfg => {
        const controlInputs: {
            [k in keyof NodeControlSocketKindMap['processSolve']['inputs']]: FlowNodeInput.Opts<NodeControlSocketKindMap['processSolve']['inputs'][k]>;
        } = {
            solve: { socket: 'event', label: 'Solve Trigger' },
        };
        const dataInputs: {
            [k in keyof NodeDataSocketKindMap['processSolve']['inputs']]: FlowNodeInput.Opts<NodeDataSocketKindMap['processSolve']['inputs'][k]>;
        } = {
            materialId: { socket: 'string', label: 'Material ID' },
        };
        const ret: ReturnType<NodeSpec<NodeCfg<'processSolve'>>['inputs']> = {
            ...controlInputs,
            ...dataInputs,
        };
        return ret;
    },
    outputs: nodeCfg => {
        const controlOutputs: {
            [k in keyof NodeControlSocketKindMap['processSolve']['outputs']]: FlowNodeOutput.Opts<NodeControlSocketKindMap['processSolve']['outputs'][k]>;
        } = {
            solveFail: { socket: 'event', label: 'Solve Failed' },
            solveSuccess: { socket: 'event', label: 'Solved Succeeded' },
        };
        const dataOutputs: {
            [k in keyof NodeDataSocketKindMap['processSolve']['outputs']]: FlowNodeOutput.Opts<NodeDataSocketKindMap['processSolve']['outputs'][k]>;
        } = {};
        if (nodeCfg.controlValues.useStandardizedOutputs.solveFailReason) {
            dataOutputs.param_solveFailReason = deletableOutputHelper({
                nodeCfg,
                outputType: 'string',
                label: 'Solve Fail Reason',
                value: 'param_solveFailReason',
            });
        }

        for (const de of nodeCfg.controlValues.processParameters) {
            if (de.type === ProcessDataElementKindEnum.String) {
                dataOutputs[`param_${de.id}`] = { socket: 'string', label: de.name };
            } else if (de.type === ProcessDataElementKindEnum.Number) {
                dataOutputs[`param_${de.id}`] = { socket: 'number', label: de.name };
            } else if (de.type === ProcessDataElementKindEnum.Boolean) {
                dataOutputs[`param_${de.id}`] = { socket: 'boolean', label: de.name };
            }
        }
        const ret: ReturnType<NodeSpec<NodeCfg<'processSolve'>>['outputs']> = {
            ...controlOutputs,
            ...dataOutputs,
        };
        return ret;
    },
    validator: node => {
        const errors: Record<string, string> = {};

        if (!node.controlValues.processId) {
            errors.processId = 'Process is required';
        }

        if ('deployParamKind' in node.controlValues.location) {
            if (!node.controlValues.location.deployParamId) {
                errors.location = 'Location deployment parameter is required';
            }
        } else if (!node.controlValues.location.id) {
            errors.location = 'Location is required';
        }

        return Object.keys(errors).length === 0 ? true : errors;
    },
};
